import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";

import { BaseResponse, Pagination, QueryModel, Response, ResponseFieldError, ResponseList } from "src/app/models/api.types";

import { useApiParams, useFileDownload } from "src/app/hooks";
import { deleteSalesPromo, deleteSalesPromoPrize, getSalesPromoExportListParticipant, getSalesPromoList, getSalesPromoParticipantDetail, getSalesPromoParticipantList, getSalesPromoParticipantPrizeList, getSalesPromoParticipantSummary, getSalesPromoPrizeDetail, getSalesPromoPrizeList, getSalesPromoRule, getSalesPromoSelectPrizeMission, getSalesPromoSelectPrizeTransaction, randomizeSalesPromoSelectPrizeTransaction, salesPromoDetail, salesPromoDetailWinnerExport, salesPromoDetailWinnerResult, saveSalesPromoGeneral, saveSalesPromoRule, submitSalesPromoPrize, submitSalesPromoSelectPrizeMission, submitSalesPromoSelectPrizeTransaction, submitSummarySalesPromo, URL_CAMPAIGN_SALES_PROMO, URL_CAMPAIGN_SALES_PROMO_PARTICIPANT, URL_CAMPAIGN_SALES_PROMO_PARTICIPANT_DETAIL, URL_CAMPAIGN_SALES_PROMO_PARTICIPANT_EXPORT, URL_CAMPAIGN_SALES_PROMO_PARTICIPANT_SUMMARY, URL_CAMPAIGN_SALES_PROMO_PRIZES, URL_CAMPAIGN_SALES_PROMO_SELECT_PRIZE_MISSION, URL_CAMPAIGN_SALES_PROMO_SELECT_PRIZE_RANDOMIZE, URL_CAMPAIGN_SALES_PROMO_SELECT_PRIZE_TRANSACTION, URL_CAMPAIGN_SALES_PROMO_WINNERS, URL_CAMPAIGN_SALES_PROMO_WINNERS_EXPORT } from "../services/Campaign.service";

import { PrizeListModel, SalesPromoModel, SalesPromoParticipant, SalesPromoPrizeModel, SalesPromoResult, SalesPromoRuleMasterModel, SalesPromoRuleModel, SalesPromoSelectGiftModel, SalesPromoSelectGiftPrizesModel } from '../models/SalesPromo.model';
import { selectDetail, selectList, useRefetchFeatured } from './LuckyDraw.provider';
import { createSelector } from "reselect";
import { defaultSalesPromoPrizes, defaultSalesPromoPrizeTranx, defaultSalesPromoRuleValues } from "../page/Campaign/SalesPromoForm/SalesPromoForm3.hook";

export function useGetSalesPromoList(param?: QueryModel) {

  const defaultParams = {
    directory_ids: '',
    period: '',
    is_direct_cashback_point: '',
    is_mission_collect_stamp: ''
  };
  const [_params] = useApiParams({ defaultValues: defaultParams });
  const params = { ...param, ..._params };

  return useQuery<
    AxiosResponse<BaseResponse<Pagination<SalesPromoModel[]>>>,
    AxiosError<BaseResponse<Response<any>>>,
    ResponseList<SalesPromoModel[]> | undefined
  >({
    keepPreviousData: true,
    queryKey: [URL_CAMPAIGN_SALES_PROMO, params],
    queryFn: (_) => getSalesPromoList(params, _.signal),
    select: selectList,
  });
}

export function useSalesPromoDetailGeneralInfo(id?: string, is_full_info?: boolean) {

  return useQuery<
    AxiosResponse<BaseResponse<Response<SalesPromoModel>>>,
    AxiosError<BaseResponse<Response<SalesPromoModel>>>,
    any | undefined
  >({
    keepPreviousData: false,
    queryKey: [`${URL_CAMPAIGN_SALES_PROMO}/${id}`],
    queryFn: (_) => salesPromoDetail(id ?? '', { is_full_info }, _.signal),
    select: selectDetail,
    enabled: !!id
  });
}

export function useSalesPromoParticipantList(campaign_id: string, param?: QueryModel) {
  const [_params] = useApiParams();
  const params = { ...param, ..._params };
  delete params.start_date;
  delete params.start_date;

  return useQuery<
    AxiosResponse<BaseResponse<Pagination<SalesPromoParticipant[]>>>,
    AxiosError<BaseResponse<Response<any>>>,
    ResponseList<SalesPromoParticipant[]> | undefined
  >({
    keepPreviousData: true,
    queryKey: [URL_CAMPAIGN_SALES_PROMO_PARTICIPANT(campaign_id), params],
    queryFn: (_) => getSalesPromoParticipantList(campaign_id, params, _.signal),
    select: selectList,
  });
}

export function useGetSalesPromoDetailParticipantSummary(campaign_id: string) {
  const defaultParams = {
    time: '',
    start_date: '',
    end_date: ''
  };
  const [_params] = useApiParams({ defaultValues: defaultParams });
  const params = { ..._params };
  delete params.sort;
  delete params.order;
  delete params.page;
  delete params.limit;
  delete params.search;

  return useQuery<
    AxiosResponse<BaseResponse<Response<SalesPromoParticipant>>>,
    AxiosError<BaseResponse<Response<SalesPromoParticipant>>>,
    any | undefined
  >({
    keepPreviousData: false,
    queryKey: [URL_CAMPAIGN_SALES_PROMO_PARTICIPANT_SUMMARY(campaign_id), params],
    queryFn: (_) => getSalesPromoParticipantSummary(campaign_id, params, _.signal),
    select: selectDetail,
    enabled: !!campaign_id
  });
}

export function useExportSalesPromoParticipantList(campaign_id: string) {
  const defaultValues = {
    search: '',
    sort: '',
    order: ''
  };
  const [_params] = useApiParams({
    defaultValues,
  });
  const params = {
    ..._params,
    page: undefined,
    limit: undefined
  };

  return useFileDownload<unknown>({
    mutationKey: [URL_CAMPAIGN_SALES_PROMO_PARTICIPANT_EXPORT(campaign_id)],
    mutationFn: (_) => getSalesPromoExportListParticipant(campaign_id, { ...params })
  });
}

export function useGetSalesPromoDetailParticipantDetail(campaign_id: string, participant_id: string) {
  return useQuery<
    AxiosResponse<BaseResponse<Response<SalesPromoParticipant>>>,
    AxiosError<BaseResponse<Response<SalesPromoParticipant>>>,
    SalesPromoParticipant | undefined
  >({
    keepPreviousData: false,
    queryKey: [URL_CAMPAIGN_SALES_PROMO_PARTICIPANT_DETAIL(campaign_id, participant_id)],
    queryFn: (_) => getSalesPromoParticipantDetail(campaign_id, participant_id, _.signal),
    select: selectDetail,
    enabled: !!participant_id && !!campaign_id
  });
}

export function useSalesPromoParticipantPrizeList(campaign_id: string, participant_id: string) {
  const defaultValues = {
    directory_ids: '',
    prize_statuses: ''
  };
  const [_params] = useApiParams({
    defaultValues,
  });
  const params = { ..._params, claim_directory_ids: _params.directory_ids, statuses: _params.prize_statuses };
  delete params.directory_ids
  delete params.prize_statuses

  return useQuery<
    AxiosResponse<BaseResponse<Pagination<SalesPromoPrizeModel[]>>>,
    AxiosError<BaseResponse<Response<any>>>,
    ResponseList<SalesPromoPrizeModel[]> | undefined
  >({
    keepPreviousData: true,
    queryKey: [`${URL_CAMPAIGN_SALES_PROMO_PARTICIPANT_DETAIL(campaign_id, participant_id)}/prizes`, params],
    queryFn: (_) => getSalesPromoParticipantPrizeList(campaign_id, participant_id, params, _.signal),
    select: selectList,
  });
}


export function useSaveSalesPromoGeneral() {

  return useMutation<
    AxiosResponse<BaseResponse<Response<SalesPromoModel>>>,
    AxiosError<BaseResponse<ResponseFieldError>>,
    SalesPromoModel
  >({
    mutationKey: [URL_CAMPAIGN_SALES_PROMO],
    mutationFn: saveSalesPromoGeneral
  });
}

export function useDeleteSalesPromo() {
  return useMutation<
    AxiosResponse<BaseResponse<Response<SalesPromoModel>>>,
    AxiosError<BaseResponse<ResponseFieldError>>,
    string // Pass `id` as a variable when calling mutate
  >({
    mutationFn: (id) => deleteSalesPromo(id), // Correct function reference
  });
}

export function useGetSalesPromoPrizeList(campaign_id: string, param?: QueryModel) {

  const defaultParams = {
    directory_ids: '',
    draw_date: '',
    types: '',
    valid_until: '',
  };
  const [_params] = useApiParams({ defaultValues: defaultParams });
  const params = { ...param, ..._params };
  return useGetSalesPromoPrizeMasterList(campaign_id, params);
}

export function useGetSalesPromoPrizeMasterList(campaign_id: string, param?: QueryModel, additional_param?: object) {
  const params = { ...param, ...additional_param };
  return useQuery<
    AxiosResponse<BaseResponse<Pagination<SalesPromoPrizeModel[]>>>,
    AxiosError<BaseResponse<Response<any>>>,
    ResponseList<SalesPromoPrizeModel[]> | undefined
  >({
    keepPreviousData: true,
    queryKey: [URL_CAMPAIGN_SALES_PROMO_PRIZES(campaign_id), params],
    queryFn: (_) => getSalesPromoPrizeList(params, campaign_id, _.signal),
    select: selectList,
  });
}

export const useSubmitSalesPromoPrize = (campaign_id: string) => {
  const refetchList = useRefetchFeatured(URL_CAMPAIGN_SALES_PROMO_PRIZES(campaign_id));
  return useMutation<
    AxiosResponse<BaseResponse<Response<SalesPromoPrizeModel>>>,
    AxiosError<BaseResponse<ResponseFieldError>>,
    any
  >({
    mutationKey: [URL_CAMPAIGN_SALES_PROMO_PRIZES(campaign_id)],
    mutationFn: (payload) => submitSalesPromoPrize(campaign_id, payload),
    onSuccess: refetchList
  })
}

export const useDeleteSalesPromoPrize = (campaign_id: string) => {
  const refetchList = useRefetchFeatured(URL_CAMPAIGN_SALES_PROMO_PRIZES(campaign_id));
  return useMutation<
    AxiosResponse<BaseResponse<Response<SalesPromoPrizeModel>>>,
    AxiosError<BaseResponse<ResponseFieldError>>,
    { campaign_id: string, prize_id: string, prize_type: 'rewards' | 'product' } // Pass `prize_id` as a variable when calling mutate
  >({
    mutationFn: ({ campaign_id, prize_id, prize_type }) => deleteSalesPromoPrize(campaign_id, prize_id, prize_type),
    onSuccess: refetchList
  });
}


const selectSalesPromoRules = createSelector(
  (state: AxiosResponse<BaseResponse<Pagination<SalesPromoRuleModel[]>>>) => state.data,
  (data: BaseResponse<Pagination<SalesPromoRuleModel[]>>) => {
    const content = data.response_output?.list?.content?.length
      ? data.response_output.list.content
      : defaultSalesPromoRuleValues.prize_rules;

    content.forEach((rule) => {
      if (rule.sales_promo_rules && rule.sales_promo_rules.length > 0) {
        rule.sales_promo_rules = rule.sales_promo_rules.map((trx) => ({
          ...trx,
          prizes: (trx.prizes?.length ? trx.prizes : defaultSalesPromoPrizes).map((item) => {
            let updatedPrizes;
            if (item.type === "PRODUCT") {
              updatedPrizes = { id: "PRODUCT", name: "Direct Gift" };
            } else if (item.type === "STAMP") {
              updatedPrizes = { id: "STAMP", name: "STAMP" };
            } else {
              updatedPrizes = item.prizes ? (item.prizes as any)[0] : undefined;
            }
            return {
              ...item,
              prizes: updatedPrizes,
            };
          })
        }));
      } else {
        rule.sales_promo_rules = defaultSalesPromoPrizeTranx;
      }
    });
    return { prize_rules: content } as SalesPromoRuleMasterModel
  }
);

export function useGetSalesPromoRules(id?: string) {
  return useQuery<
    AxiosResponse<BaseResponse<Pagination<SalesPromoRuleModel[]>>>,
    AxiosError<BaseResponse<Response<any>>>,
    SalesPromoRuleMasterModel | undefined
  >({
    keepPreviousData: true,
    queryKey: [`${URL_CAMPAIGN_SALES_PROMO}/${id}/rules`],
    queryFn: (_) => getSalesPromoRule(id ?? '', _.signal),
    select: selectSalesPromoRules,
    enabled: !!id,
  });
}

export const useSaveSalesPromoRule = (campaign_id: string) => {
  return useMutation<
    AxiosResponse<BaseResponse<Pagination<SalesPromoModel[]>>>,
    AxiosError<BaseResponse<ResponseFieldError>>,
    SalesPromoModel[]
  >({
    mutationKey: [`${URL_CAMPAIGN_SALES_PROMO}/${campaign_id}/rules`],
    mutationFn: (payload) => saveSalesPromoRule(campaign_id, payload),
  })
}

export function useGetPrizeMasterListSalesPromo(campaign_id: string, param?: QueryModel, additional_param?: object) {
  const params = { ...param, ...additional_param };
  return useQuery<
    AxiosResponse<BaseResponse<Pagination<PrizeListModel[]>>>,
    AxiosError<BaseResponse<Response<any>>>,
    ResponseList<PrizeListModel[]> | undefined
  >({
    keepPreviousData: true,
    queryKey: [URL_CAMPAIGN_SALES_PROMO_PRIZES(campaign_id), params],
    queryFn: (_) => getSalesPromoPrizeList(params, campaign_id, _.signal),
    select: selectList,
  });
}

export function useSalesPromoDetailPrizeItem(campaign_id?: string, prize_id?: string, type?: 'rewards' | 'product', onSuccess?: (data: any) => void) {

  return useQuery<
    AxiosResponse<BaseResponse<Response<SalesPromoPrizeModel>>>,
    AxiosError<BaseResponse<Response<SalesPromoPrizeModel>>>,
    any | undefined
  >({
    keepPreviousData: false,
    queryKey: [`${URL_CAMPAIGN_SALES_PROMO_PRIZES(campaign_id)}/${prize_id}/${type}`],
    queryFn: (_) => getSalesPromoPrizeDetail(campaign_id, prize_id, type, _.signal),
    select: selectDetail,
    enabled: (!!campaign_id && !!prize_id && !!type),
    onSuccess
  });
}

export function useSubmitSummarySalesPromo() {
  return useMutation<
    AxiosResponse<BaseResponse<Response<SalesPromoModel>>>,
    AxiosError<BaseResponse<ResponseFieldError>>,
    string
  >({
    mutationFn: (campaign_id) => submitSummarySalesPromo(campaign_id)
  });
}

export function useSalesPromoSelectPrizeMission(campaign_id?: string, member_id?: string, mission_stamp_id?: string, onSuccess?: (data: any) => void, onError?: (error: AxiosError<BaseResponse<Response<SalesPromoSelectGiftModel>>>) => void) {

  return useQuery<
    AxiosResponse<BaseResponse<Response<SalesPromoSelectGiftModel>>>,
    AxiosError<BaseResponse<Response<SalesPromoSelectGiftModel>>>,
    any | undefined
  >({
    keepPreviousData: false,
    queryKey: [URL_CAMPAIGN_SALES_PROMO_SELECT_PRIZE_MISSION(campaign_id!,member_id!,mission_stamp_id!)],
    queryFn: (_) => getSalesPromoSelectPrizeMission(campaign_id!, member_id!, mission_stamp_id!, _.signal),
    select: selectDetail,
    enabled: (!!campaign_id && !!member_id && !!mission_stamp_id),
    onSuccess,
    onError
  });
}

export const useSubmitSalesPromoPrizeMission = (campaign_id?: string, member_id?: string, mission_stamp_id?: string) => {
  const refetchList = useRefetchFeatured(URL_CAMPAIGN_SALES_PROMO_PARTICIPANT_DETAIL(campaign_id!,member_id!));
  return useMutation<
    AxiosResponse<BaseResponse<Response<SalesPromoSelectGiftModel>>>,
    AxiosError<BaseResponse<ResponseFieldError>>,
    any
  >({
    mutationKey: [URL_CAMPAIGN_SALES_PROMO_SELECT_PRIZE_MISSION(campaign_id!,member_id!,mission_stamp_id!)],
    mutationFn: (payload) => submitSalesPromoSelectPrizeMission(campaign_id!,member_id!,mission_stamp_id!, payload),
    onSuccess: refetchList
  })
}

export function useSalesPromoSelectPrizeTransaction(campaign_id?: string, member_id?: string, member_sales_promo_id?: string, onSuccess?: (data: any) => void, onError?: (error: AxiosError<BaseResponse<Response<SalesPromoSelectGiftModel>>>) => void) {

  return useQuery<
    AxiosResponse<BaseResponse<Response<SalesPromoSelectGiftModel>>>,
    AxiosError<BaseResponse<Response<SalesPromoSelectGiftModel>>>,
    any | undefined
  >({
    keepPreviousData: false,
    queryKey: [URL_CAMPAIGN_SALES_PROMO_SELECT_PRIZE_TRANSACTION(campaign_id!,member_id!,member_sales_promo_id!)],
    queryFn: (_) => getSalesPromoSelectPrizeTransaction(campaign_id!, member_id!, member_sales_promo_id!, _.signal),
    select: selectDetail,
    enabled: (!!campaign_id && !!member_id && !!member_sales_promo_id),
    onSuccess,
    onError
  });
}

export const useSubmitSalesPromoPrizeTransaction = (campaign_id?: string, member_id?: string, member_sales_promo_id?: string) => {
  const refetchList = useRefetchFeatured(`${URL_CAMPAIGN_SALES_PROMO_PARTICIPANT_DETAIL(campaign_id!, member_id!)}/prizes`);
  return useMutation<
    AxiosResponse<BaseResponse<Response<SalesPromoSelectGiftModel>>>,
    AxiosError<BaseResponse<ResponseFieldError>>,
    any
  >({
    mutationKey: [URL_CAMPAIGN_SALES_PROMO_SELECT_PRIZE_TRANSACTION(campaign_id!,member_id!,member_sales_promo_id!)],
    mutationFn: (payload) => submitSalesPromoSelectPrizeTransaction(campaign_id!,member_id!,member_sales_promo_id!, payload),
    onSuccess: refetchList
  })
}

export const useRandomizeSalesPromoPrizeTransaction = (campaign_id?: string, member_id?: string) => {
  return useMutation<
    AxiosResponse<BaseResponse<Response<SalesPromoSelectGiftPrizesModel>>>,
    AxiosError<BaseResponse<ResponseFieldError>>,
    any
  >({
    mutationKey: [URL_CAMPAIGN_SALES_PROMO_SELECT_PRIZE_RANDOMIZE(campaign_id!,member_id!)],
    mutationFn: (payload) => randomizeSalesPromoSelectPrizeTransaction(campaign_id!,member_id!, payload),
    onSuccess(data, variables, context) {
      return data.data.response_output?.detail
    },
  })
}

export function useSalesPromoDetailWinnerResult(campaign_id?: string) {

  return useQuery<
    AxiosResponse<BaseResponse<Response<SalesPromoResult>>>,
    AxiosError<BaseResponse<Response<SalesPromoResult>>>,
    any | undefined
  >({
    keepPreviousData: false,
    queryKey: [URL_CAMPAIGN_SALES_PROMO_WINNERS(campaign_id ?? '')],
    queryFn: (_) => salesPromoDetailWinnerResult(campaign_id ?? '', _.signal),
    select: selectDetail,
    enabled: !!campaign_id
  });
}

export function useSalesPromoDetailWinnerExport(campaign_id: string) {

  return useFileDownload<unknown>({
    mutationKey: [URL_CAMPAIGN_SALES_PROMO_WINNERS_EXPORT(campaign_id)],
    mutationFn: (_) => salesPromoDetailWinnerExport(campaign_id)
  });
}